import {
  Box,
  Button,
  Divider,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { CustomInput } from "global/components";
import {
  handleErrorMessage,
  isTruthy,
  openSuccessNotification,
} from "helpers/methods";
import { UserFields } from "models/interfaces";
import { useEffect, useState } from "react";
import history from "utils/history";
import settingsStyles from "../Settings.styles";
import strings from "global/constants/StringConstants";
import CustomDialog from "global/components/CustomDialog/CustomDialog";
import viewpaths from "global/constants/ViewPathConstants";
import CustomLoader from "global/components/CustomLoader/CustomLoader";
import { getActiveCampaigner, insertCampaigner } from "./UsersService";
import CustomAutoComplete from "global/components/CustomAutoComplete/CustomAutoComplete";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;

const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

interface CustomProps {
  user: UserFields;
  visible: boolean;
  handleDialogClose: Function;
  handleGetUsers: Function;
  allRoles: { id: number; name: string }[];
  isEdit: boolean;
}

const InviteUser = (props: CustomProps) => {
  const theme = useTheme();
  const classes = settingsStyles(theme);
  const emailRegex = strings.regex;
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [managers, setManagers] = useState<{ email: string; userId: number }[]>(
    []
  );
  const [fields, setFields] = useState<UserFields>({
    email: "",
    name: "",
    firstName: "",
    lastName: "",
    managedBy: "",
    managerId: 0,
    roleIds: [],
    allowedEmailCount: "",
  });
  const [errors, setErrors] = useState<{ [key: string]: any }>({
    email: "",
    firstName: "",
    lastName: "",
    contactNo: "",
    managedBy: "",
    roleIds: [],
    allowedEmailCount: "",
  });
  const [selectedRole, setSelectedRole] = useState<string | null>("");

  useEffect(() => {
    if (props.visible) {
      retrieveAllUsersList();
    }
  }, [props.visible]);

  const retrieveAllUsersList = async () => {
    try {
      const users = await getActiveCampaigner();
      setManagers(users);
    } catch (error: any) {
      setIsLoading(false);
      handleErrorMessage(error);
    }
  };

  const handleOnChangeInputField = (e: any) => {
    const { value } = e.target;
    const spaceSeparatedValue = value.replace(/\s+/g, "");
    setFields({ ...fields, [e.target.name]: spaceSeparatedValue });
    setErrors({
      ...errors,
      [e.target.name]: "",
    });
  };

  const handleManagerSelect = (manager: { id: number; label: string }) => {
    setFields({
      ...fields,
      managedBy: manager?.label || "",
      managerId: manager?.id || 0,
    });
    setErrors({
      ...errors,
      managedBy: "",
    });
  };

  const handleSelectChange = (event: any) => {
    const parsedRole = JSON.parse(event?.target.value);
    setSelectedRole(parsedRole.name);
    setFields({
      ...fields,
      [event.target.name]: [parsedRole],
    });
  };

  const handleInputBlur = (e: any) => {
    setFields({ ...fields, [e.target.name]: e.target.value.trim() });
  };

  const handleValidate = () => {
    let error = { ...errors };
    let isValid: boolean = true;
    if (!fields.firstName) {
      error.firstName = "Please enter first name";
      isValid = false;
    }
    if (!fields.email) {
      error.email = "Please enter email";
      isValid = false;
    }
    if (!emailRegex.test(fields.email)) {
      error.email = "Please enter valid email";
      isValid = false;
    }
    if (fields.roleIds.length === 0) {
      error.roleIds = "Please select role";
      isValid = false;
    }
    if (!props.isEdit && !fields.managedBy) {
      error.managedBy = "Please select the manager email";
      isValid = false;
    }
    if (!fields.allowedEmailCount) {
      error.allowedEmailCount = "Please enter allowed email count";
      isValid = false;
    }
    setErrors(error);
    return isValid;
  };

  const handleAddUser = async () => {
    if (handleValidate()) {
      try {
        setIsLoading(true);
        const body: any = {
          email: fields.email,
          firstName: fields.firstName?.trim(),
          lastName: fields.lastName?.trim(),
          roleIds: fields.roleIds.map(
            (role: { id: number; name: string }) => role.id
          ),
          allowedEmailCount: +fields.allowedEmailCount,
          managedBy: fields.managedBy,
          managerId: fields.managerId,
        };
        await insertCampaigner(body);
        openSuccessNotification("Invite sent successfully!");
        history.push(`${viewpaths.settingsViewPath}/users`);
        handleDialogClose();
        props.handleGetUsers(1);
        setIsLoading(false);
      } catch (error: any) {
        setIsLoading(false);
        handleErrorMessage(error);
      }
    }
  };

  const handleDialogClose = () => {
    setSelectedRole("");
    setFields({
      email: "",
      name: "",
      firstName: "",
      lastName: "",
      allowedEmailCount: "",
      managedBy: "",
      managerId: 0,
      roleIds: [],
    });
    setErrors({
      email: "",
      name: "",
      firstName: "",
      lastName: "",
      allowedEmailCount: "",
      assignBy: "",
      roleIds: [],
    });
    props.handleDialogClose();
  };

  const getHeader = () => {
    return (
      <Box>
        <Typography
          variant="h6"
          fontWeight={600}
          color={theme.palette.primary.main}
          pb={2}
        >
          {"Send Invite"}
        </Typography>
        <Divider />
      </Box>
    );
  };

  const getFields = () => {
    return (
      <>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Box sx={classes.labelBox}>
              <CustomInput
                required
                label="First Name"
                id="first-name"
                placeHolder="Enter first name"
                type="text"
                name="firstName"
                onChange={handleOnChangeInputField}
                onBlur={handleInputBlur}
                value={fields.firstName}
                propsToInputElement={{ minLength: 1, maxLength: 30 }}
              />
            </Box>
            {isTruthy(errors.firstName) && errors.firstName && (
              <FormHelperText error>{errors.firstName}</FormHelperText>
            )}
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={classes.labelBox}>
              <CustomInput
                id="last-name"
                label="Last Name"
                placeHolder="Enter last name"
                type="text"
                name="lastName"
                onChange={handleOnChangeInputField}
                onBlur={handleInputBlur}
                value={fields.lastName}
                propsToInputElement={{ minLength: 1, maxLength: 30 }}
              />
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={classes.labelBox}>
              <CustomInput
                required
                id="email"
                label="Email"
                placeHolder="user@example.com"
                type="text"
                name="email"
                onChange={handleOnChangeInputField}
                onBlur={handleInputBlur}
                value={fields.email}
                propsToInputElement={{ minLength: 1, maxLength: 255 }}
              />
            </Box>
            {isTruthy(errors.email) && errors.email && (
              <FormHelperText error>{errors.email}</FormHelperText>
            )}
          </Grid>
          <Grid item xs={12} md={6}>
            <InputLabel
              required
              sx={classes.inputLabel}
              style={{ marginBottom: 8 }}
            >
              Manager
            </InputLabel>
            {!(props.isEdit && !isTruthy(props.user.managedBy)) && (
              <CustomAutoComplete
                textFieldVariant="outlined"
                id="managedBy"
                placeHolder="Select Manager Email"
                options={managers.map((manager: any) => ({
                  label: manager.email,
                  id: manager.userId,
                }))}
                value={fields.managedBy}
                onChange={(event: any, newValue: any) => {
                  handleManagerSelect(newValue);
                }}
                errorMessage={errors.managedBy}
              />
            )}
          </Grid>
          <Grid item xs={12} md={6}>
            <InputLabel required sx={classes.inputLabel}>
              Role
            </InputLabel>
            <Select
              required
              fullWidth
              sx={classes.select}
              labelId="role-id"
              id="role-id"
              name="roleIds"
              value={selectedRole || ""}
              onChange={handleSelectChange}
              renderValue={(selected) =>
                selected ? (
                  <Typography variant="body2">{selected}</Typography>
                ) : (
                  <Typography variant="body2" color="#bbbfc6">
                    Select Role
                  </Typography>
                )
              }
              displayEmpty
              MenuProps={MenuProps}
              error={
                !isTruthy(fields.roleIds[0]) && errors.roleIds.length !== 0
              }
            >
              {props.allRoles.map((role: any) => (
                <MenuItem
                  key={role.name}
                  value={JSON.stringify({ id: role.id, name: role.name })}
                  sx={classes.dropDownMenuItem}
                >
                  {role.name}
                </MenuItem>
              ))}
            </Select>

            {!isTruthy(fields.roleIds[0]) && (
              <FormHelperText error>{errors.roleIds}</FormHelperText>
            )}
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={classes.labelBox}>
              <CustomInput
                required
                label="Emails Allowed Per Day"
                id="allowedEmailCount"
                type="number"
                placeHolder="Enater allowed emails count/day"
                name="allowedEmailCount"
                onChange={(event: any) => {
                  event.target.value = Math.abs(event.target.value);
                  handleOnChangeInputField(event);
                }}
                onBlur={handleInputBlur}
                value={fields.allowedEmailCount}
                InputProps={{ type: "number", min: 0 }}
              />
            </Box>
            {!isTruthy(fields.allowedEmailCount) &&
              errors.allowedEmailCount && (
                <FormHelperText error>
                  {errors.allowedEmailCount}
                </FormHelperText>
              )}
          </Grid>
        </Grid>
      </>
    );
  };

  const getFooter = () => {
    return (
      <Stack direction="row" spacing={2}>
        <Button
          id="contacts_add_save_btn"
          variant="outlined"
          onClick={() => handleDialogClose()}
          sx={{
            width: "7.125rem",
            height: "2.5rem",
          }}
        >
          Cancel
        </Button>
        <Button
          id="save"
          variant="contained"
          size="small"
          onClick={handleAddUser}
          disabled={isLoading}
          sx={{
            width: "7.125rem",
            height: "2.5rem",
          }}
        >
          {props.user.email ? "Save" : "Send"}
        </Button>
      </Stack>
    );
  };

  return (
    <>
      <CustomLoader isLoading={isLoading} />
      <CustomDialog
        title={getHeader()}
        handleDialogClose={() => handleDialogClose()}
        isDialogOpen={props.visible}
        body={getFields()}
        footer={getFooter()}
        closeButtonVisibility={true}
        closable
        maxWidth="md"
        persistOnClickOutside={true}
      />
    </>
  );
};

export default InviteUser;
